import type { PortfolioCustomerSummary } from "@/v2-backoffice-client/portfolio/portfolio.utils"

/** The profile window every audience read is anchored on. */
export const AUDIENCE_WINDOW_DAYS = 28

/** One how-often bucket of the monthly actives. */
export type PortfolioAudienceFrequencyBand = {
  key: "near-daily" | "weekly-plus" | "occasional" | "sampled"
  label: string
  /** ActiveDays28d range covered, inclusive. */
  minDays: number
  count: number
}

export type PortfolioAudience = {
  /** Monthly actives: users with any tracked activity in 28 days. */
  mau: number
  /** Weekly actives: users with tracked activity in the last 7 days. */
  wau: number
  /**
   * Mean distinct users per day over the window (Σ active days ÷ 28) —
   * the DAU headline. Exact: ActiveDays28d counts each user's distinct
   * activity days.
   */
  avgDau: number
  /** Busiest day of the strip (from the aligned per-user day series). */
  peakDau: number
  /** Distinct users active per day, last 28 days oldest first. */
  dauSeries: number[]
  /** avgDau ÷ mau, 0–1 — the daily-habit ratio; null when nobody active. */
  stickiness: number | null
  /** How often the monthly actives showed up, most habitual first. */
  frequencyBands: PortfolioAudienceFrequencyBand[]
  /** Median activity days among the monthly actives. */
  medianActiveDays: number | null
  /**
   * Weighted actions per user per active day — the session-depth proxy.
   * Wall-clock duration is NOT tracked (no session-end signal), so this
   * is the honest intensity read: how much a user does on a day they show
   * up. Null before the weighting rollout.
   */
  depthPerActiveDay: number | null
}

/**
 * Fleet-level audience size and habit: DAU/WAU/MAU, stickiness, the
 * day-by-day DAU strip, how often actives return, and depth per active
 * day. All exact from the nightly profiles except the strip, which reads
 * each user's weighted day series (a day with only unweighted legacy
 * events can show as quiet).
 */
export function derivePortfolioAudience(
  customers: PortfolioCustomerSummary[],
): PortfolioAudience {
  const dauSeries: number[] = Array.from(
    { length: AUDIENCE_WINDOW_DAYS },
    () => 0,
  )
  let mau = 0
  let wau = 0
  let activeDaysTotal = 0
  let weightedTotal = 0
  let weightedSeen = false
  const activeDaysPerUser: number[] = []
  const bands: PortfolioAudienceFrequencyBand[] = [
    { key: "near-daily", label: "Near-daily", minDays: 15, count: 0 },
    { key: "weekly-plus", label: "Several days a week", minDays: 8, count: 0 },
    { key: "occasional", label: "A few days a month", minDays: 3, count: 0 },
    { key: "sampled", label: "Dropped in once or twice", minDays: 1, count: 0 },
  ]

  for (const customer of customers) {
    for (const member of customer.members) {
      const activeDays = member.ActiveDays28d ?? 0
      if (activeDays <= 0 && (member.Events28d ?? 0) <= 0) continue
      mau += 1
      if ((member.Events7d ?? 0) > 0) wau += 1
      activeDaysTotal += activeDays
      activeDaysPerUser.push(activeDays)
      if (member.WeightedEvents28d != null) {
        weightedTotal += member.WeightedEvents28d
        weightedSeen = true
      }
      // Bands cover >= 1; the Events28d-only edge (0 active days) lands in
      // the lightest band rather than vanishing.
      const band =
        bands.find((candidate) => activeDays >= candidate.minDays) ??
        bands[bands.length - 1]
      band.count += 1

      const daily = member.Daily28d ?? []
      // Right-aligned: the last slot is the newest day for every profile
      // (all computed by the same nightly run).
      const offset = AUDIENCE_WINDOW_DAYS - daily.length
      for (let i = 0; i < daily.length; i += 1) {
        const slot = offset + i
        if (slot >= 0 && daily[i] > 0) dauSeries[slot] += 1
      }
    }
  }

  const avgDau = activeDaysTotal / AUDIENCE_WINDOW_DAYS
  const sortedActiveDays = [...activeDaysPerUser].sort((a, b) => a - b)
  return {
    mau,
    wau,
    avgDau,
    peakDau: Math.max(0, ...dauSeries),
    dauSeries,
    stickiness: mau > 0 ? avgDau / mau : null,
    frequencyBands: bands,
    medianActiveDays:
      sortedActiveDays.length > 0
        ? sortedActiveDays[Math.floor((sortedActiveDays.length - 1) / 2)]
        : null,
    depthPerActiveDay:
      weightedSeen && activeDaysTotal > 0
        ? weightedTotal / activeDaysTotal
        : null,
  }
}
